import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react';

const ImageCacheContext = createContext(null);

const MAX_CACHE_SIZE = 100; // Máximo de 100 imagens em cache
// Teto por bytes: o limite por quantidade permitia ~70 MB de blobs de fotos
// full-size presos na memória. A grade não usa mais este cache (usa `<img>` com
// cache HTTP); ele atende só o preview ampliado.
const MAX_CACHE_BYTES = 40 * 1024 * 1024; // 40 MB
const MAX_CACHE_AGE = 30 * 60 * 1000; // 30 minutos

/**
 * Provedor de cache global para Blob URLs de imagens
 * Mantém as imagens em cache para evitar recarregamentos desnecessários
 */
export const ImageCacheProvider = ({ children }) => {
  // Cache: Map<url, { blobUrl, timestamp, size }>
  const cacheRef = useRef(new Map());

  // Limpa cache de URLs antigas
  const cleanOldCache = useCallback(() => {
    const now = Date.now();
    const entries = Array.from(cacheRef.current.entries());

    const remover = ([url, data]) => {
      URL.revokeObjectURL(data.blobUrl);
      cacheRef.current.delete(url);
    };

    // Remove itens expirados
    entries.forEach(([url, data]) => {
      if (now - data.timestamp > MAX_CACHE_AGE) remover([url, data]);
    });

    const restantes = Array.from(cacheRef.current.entries()).sort(
      (a, b) => a[1].timestamp - b[1].timestamp
    );

    let totalBytes = restantes.reduce((soma, [, data]) => soma + (data.size || 0), 0);

    // Remove os mais antigos até caber nos dois limites (quantidade e bytes).
    while (
      restantes.length > 0 &&
      (restantes.length > MAX_CACHE_SIZE || totalBytes > MAX_CACHE_BYTES)
    ) {
      const maisAntigo = restantes.shift();
      totalBytes -= maisAntigo[1].size || 0;
      remover(maisAntigo);
    }
  }, []);

  // Obtém item do cache
  const getFromCache = useCallback((url) => {
    const cached = cacheRef.current.get(url);
    if (cached) {
      // Atualiza timestamp para manter item "quente"
      cached.timestamp = Date.now();
      return cached.blobUrl;
    }
    return null;
  }, []);

  // Adiciona item ao cache
  const addToCache = useCallback(
    (url, blobUrl, size = 0) => {
      cleanOldCache();
      cacheRef.current.set(url, {
        blobUrl,
        timestamp: Date.now(),
        size,
      });
    },
    [cleanOldCache]
  );

  // Remove item específico do cache
  const removeFromCache = useCallback((url) => {
    const cached = cacheRef.current.get(url);
    if (cached) {
      URL.revokeObjectURL(cached.blobUrl);
      cacheRef.current.delete(url);
    }
  }, []);

  // Limpa todo o cache
  const clearCache = useCallback(() => {
    cacheRef.current.forEach((data) => {
      URL.revokeObjectURL(data.blobUrl);
    });
    cacheRef.current.clear();
  }, []);

  // Obtém estatísticas do cache
  const getCacheStats = useCallback(() => {
    const entries = Array.from(cacheRef.current.values());
    return {
      size: cacheRef.current.size,
      totalBytes: entries.reduce((sum, item) => sum + item.size, 0),
      maxSize: MAX_CACHE_SIZE,
      maxBytes: MAX_CACHE_BYTES,
    };
  }, []);

  // Limpa cache periodicamente
  useEffect(() => {
    const interval = setInterval(cleanOldCache, 5 * 60 * 1000); // A cada 5 minutos
    return () => clearInterval(interval);
  }, [cleanOldCache]);

  // Limpa todo o cache quando o componente desmontar
  useEffect(() => {
    return () => {
      clearCache();
    };
  }, [clearCache]);

  const value = {
    getFromCache,
    addToCache,
    removeFromCache,
    clearCache,
    getCacheStats,
  };

  return <ImageCacheContext.Provider value={value}>{children}</ImageCacheContext.Provider>;
};

export const useImageCache = () => {
  const context = useContext(ImageCacheContext);
  if (!context) {
    throw new Error('useImageCache must be used within ImageCacheProvider');
  }
  return context;
};
